import React, { useEffect, useState } from 'react';
import { Modal, Alert, Spin, Button, Space, Table, Tag } from 'antd';
import { BankOutlined, CheckCircleOutlined, WarningOutlined, FileProtectOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

interface LineaAsiento {
  key: string;
  concepto: string;
  debe: number | null;
  haber: number | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (numeroAsiento: number, nroComprobante: string) => void;
  tipo: 'compra' | 'servicio';
  idEmpresa: number;
  idSucursal: number;
  idLiquidacion: number;
  nroInterno: number;
  clienteNombre: string;
  subtotal: number;
  iva: number;
  retenciones: number;
}

const LABEL_MAP: Record<string, string> = {
  COMPRA_BRUTO: 'Compra Algodón Bruto',
  IVA_COMPRAS: 'IVA Compras',
  PROVEEDORES_ALGODON: 'Proveedores Algodón',
  SERVICIO_DESMOTE: 'Servicio Desmote',
  IVA_VENTAS: 'IVA Ventas',
  DEUDORES_SERVICIOS: 'Deudores Servicios',
};

function labelItem(item: string): string {
  if (item.startsWith('RETENCION_')) return `Retención / Percepción (ID ${item.split('_')[1]})`;
  return LABEL_MAP[item] ?? item;
}

const fmt = (v: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(v);

const n = (v: any) => parseFloat(v) || 0;

interface RetencionDetalle {
  importe: number;
  descripcion: string;
  tipo: string;
}

function buildLineas(
  tipo: 'compra' | 'servicio',
  subtotal: number,
  iva: number,
  retDet: RetencionDetalle[],
): LineaAsiento[] {
  const sumRet = retDet.reduce((s, r) => s + n(r.importe), 0);

  if (tipo === 'compra') {
    const lineas: LineaAsiento[] = [
      { key: 'bruto', concepto: 'Compra Algodón Bruto', debe: subtotal, haber: null },
    ];
    if (iva > 0)
      lineas.push({ key: 'iva', concepto: 'IVA Compras', debe: iva, haber: null });
    retDet.forEach((r, i) =>
      lineas.push({ key: `ret_${i}`, concepto: `Ret. ${r.descripcion} (${r.tipo})`, debe: null, haber: n(r.importe) })
    );
    lineas.push({ key: 'prov', concepto: 'Proveedores Algodón', debe: null, haber: subtotal + iva - sumRet });
    return lineas;
  } else {
    const lineas: LineaAsiento[] = [
      { key: 'serv', concepto: 'Servicio Desmote', debe: null, haber: subtotal },
    ];
    if (iva > 0)
      lineas.push({ key: 'iva', concepto: 'IVA Ventas', debe: null, haber: iva });
    retDet.forEach((r, i) =>
      lineas.push({ key: `ret_${i}`, concepto: `Perc. ${r.descripcion} (${r.tipo})`, debe: null, haber: n(r.importe) })
    );
    lineas.push({ key: 'deud', concepto: 'Deudores Servicios', debe: subtotal + iva + sumRet, haber: null });
    return lineas;
  }
}

const GrabarAsientoModal: React.FC<Props> = ({
  open, onClose, onSuccess, tipo,
  idEmpresa, idSucursal, idLiquidacion, nroInterno,
  subtotal, iva,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const baseUrl = process.env.REACT_APP_LIQUIDACION_URL;

  const [faltantes, setFaltantes] = useState<string[]>([]);
  const [retDet, setRetDet] = useState<RetencionDetalle[]>([]);
  const [comprobante, setComprobante] = useState<string>('');
  const [cargando, setCargando] = useState(false);
  const [grabando, setGrabando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exito, setExito] = useState<{ numero_asiento: number; nro_comprobante: string } | null>(null);

  useEffect(() => {
    if (!open) {
      setFaltantes([]);
      setRetDet([]);
      setComprobante('');
      setError(null);
      setExito(null);
      return;
    }
    cargarDatos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cargarDatos = async () => {
    setCargando(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };

      const liqEndpoint = tipo === 'compra'
        ? `${baseUrl}/obtener_liquidacion_compraalgodon/${idEmpresa}/${idSucursal}/${idLiquidacion}/`
        : `${baseUrl}/obtener_liquidacion_serviciodesmote/${idEmpresa}/${idSucursal}/${idLiquidacion}/`;

      const [validarRes, liqRes] = await Promise.all([
        axios.post(`${baseUrl}/validar_cuentas_asiento/${tipo}/${idEmpresa}/`, {
          id_liquidacion: idLiquidacion,
          id_sucursal: idSucursal,
        }, { headers }),
        axios.get(liqEndpoint, { headers }),
      ]);

      setFaltantes(validarRes.data.faltantes ?? []);

      const liqData = liqRes.data;
      const liqObj = liqData.liquidacion ?? liqData;
      setComprobante(liqObj.comprobante ?? '');

      const retArray: RetencionDetalle[] = (liqData.retenciones ?? liqData.percepciones ?? []).map((r: any) => ({
        importe: n(r.total ?? r.importe),
        descripcion: r.retencion_info?.descripcion ?? r.descripcion ?? '',
        tipo: r.retencion_info?.tipo ?? r.tipo ?? '',
      }));
      setRetDet(retArray);

    } catch {
      setError('Error al cargar los datos del modal');
    } finally {
      setCargando(false);
    }
  };

  const handleGrabar = async () => {
    setGrabando(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await axios.post(
        `${baseUrl}/grabar_asiento/${tipo}/${idEmpresa}/${idSucursal}/${idLiquidacion}/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.grabado) {
        setExito({ numero_asiento: res.data.numero_asiento, nro_comprobante: res.data.nro_comprobante ?? '' });
      } else {
        setError(res.data.motivo ?? 'Error al grabar el asiento');
      }
    } catch (err: any) {
      setError(err?.response?.data?.motivo ?? 'Error de conexión al grabar el asiento');
    } finally {
      setGrabando(false);
    }
  };

  const puedeGrabar = !cargando && faltantes.length === 0 && !exito;
  const nroLabel = tipo === 'compra' ? `Liq. Compra N° ${nroInterno}` : `Liq. Servicio N° ${nroInterno}`;
  const lineas = buildLineas(tipo, n(subtotal), n(iva), retDet);
  const totalDebe = lineas.reduce((s, l) => s + (l.debe ?? 0), 0);
  const totalHaber = lineas.reduce((s, l) => s + (l.haber ?? 0), 0);

  const columns = [
    { title: 'Concepto', dataIndex: 'concepto', key: 'concepto' },
    {
      title: 'Debe',
      dataIndex: 'debe',
      key: 'debe',
      align: 'right' as const,
      render: (v: number | null) => v != null ? fmt(v) : '',
    },
    {
      title: 'Haber',
      dataIndex: 'haber',
      key: 'haber',
      align: 'right' as const,
      render: (v: number | null) => v != null ? fmt(v) : '',
    },
  ];

  return (
    <Modal
      open={open}
      title={
        <Space>
          <BankOutlined />
          {`Grabar Asiento Contable — ${nroLabel}`}
          {comprobante && <span style={{ fontSize: 13, color: '#666', fontWeight: 400 }}>· Cpte: {comprobante}</span>}
        </Space>
      }
      onCancel={onClose}
      footer={exito ? [
        <Button key="aceptar" type="primary" onClick={() => onSuccess(exito.numero_asiento, exito.nro_comprobante)}>
          Aceptar
        </Button>,
      ] : [
        <Button key="cancelar" onClick={onClose} disabled={grabando}>
          Cancelar
        </Button>,
        <Button
          key="grabar"
          type="primary"
          icon={<BankOutlined />}
          loading={grabando}
          disabled={!puedeGrabar}
          onClick={handleGrabar}
        >
          Grabar Asiento
        </Button>,
      ]}
      width={580}
      destroyOnClose
    >
      {cargando ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin size="large" />
        </div>
      ) : exito ? (
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <Alert
            type="success"
            icon={<CheckCircleOutlined />}
            showIcon
            message={`Asiento N° ${exito.numero_asiento} grabado correctamente`}
            description={
              exito.nro_comprobante
                ? <span><FileProtectOutlined /> Comprobante asignado: <strong>{exito.nro_comprobante}</strong></span>
                : undefined
            }
          />
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={16}>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileProtectOutlined style={{ color: '#666' }} />
            <span style={{ fontSize: 13, color: '#555' }}>Comprobante:</span>
            {comprobante
              ? <Tag color="blue">{comprobante} — con registro AFIP</Tag>
              : <Tag color="default">Sin registro AFIP — numeración automática al grabar</Tag>
            }
          </div>

          <Table
            size="small"
            dataSource={lineas}
            columns={columns}
            rowKey="key"
            pagination={false}
            bordered
            summary={() => (
              <Table.Summary.Row style={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">{fmt(totalDebe)}</Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">{fmt(totalHaber)}</Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />

          {faltantes.length > 0 ? (
            <Alert
              type="warning"
              icon={<WarningOutlined />}
              showIcon
              message="Cuentas contables faltantes"
              description={
                <ul style={{ marginBottom: 0, paddingLeft: 16 }}>
                  {faltantes.map(f => <li key={f}>{labelItem(f)}</li>)}
                </ul>
              }
            />
          ) : (
            <Alert
              type="success"
              icon={<CheckCircleOutlined />}
              showIcon
              message="Todas las cuentas están configuradas. El asiento puede grabarse."
            />
          )}

          {error && <Alert type="error" showIcon message={error} />}
        </Space>
      )}
    </Modal>
  );
};

export default GrabarAsientoModal;
